import { persistKeyForServerName } from './persistKey';

interface EnvironmentVariables {
  SERVER_NAME: string;
  JITSI_DOMAIN: string;
  PLATFORM_NAME: string;
  SERVICE_BUS_HOST: string;
  DEV_INTEGRATION_URL: string;
  API_SERVER: string;
}

declare global {
  interface Window {
    env?: EnvironmentVariables;
  }
}

function getEnvironmentVariable(
  key: keyof EnvironmentVariables,
  defaultValue: string
): string {
  if (typeof window !== 'undefined' && window.env && window.env[key]) {
    return window.env[key];
  }

  try {
    const globalProcess = (globalThis as any).process;
    if (globalProcess?.env && globalProcess.env[key]) {
      return globalProcess.env[key];
    }
  } catch (error) {
    console.debug('Process environment not available:', error);
  }

  return defaultValue;
}

// Extract core domain from SERVER_NAME (e.g., "dev.pub.harmoni.io" -> "pub.harmoni.io")
// Jitsi Meet uses only the core domain: meet.pub.harmoni.io, not meet.dev.pub.harmoni.io
function getJitsiDomain(): string {
  const serverName = getEnvironmentVariable(
    'SERVER_NAME',
    window.location.hostname.replace('www.', '')
  );
  // Remove platform prefix (first segment) to get core domain
  // "dev.pub.harmoni.io" -> "pub.harmoni.io"
  const parts = serverName.split('.');
  if (parts.length > 2 && parts[0] !== 'www') {
    return parts.slice(1).join('.');
  }
  return serverName;
}

const environment: EnvironmentVariables = {
  SERVER_NAME: getEnvironmentVariable(
    'SERVER_NAME',
    window.location.hostname.replace('www.', '')
  ),
  JITSI_DOMAIN: getJitsiDomain(),
  PLATFORM_NAME: getEnvironmentVariable('PLATFORM_NAME', 'Harmoni Cloud'),
  SERVICE_BUS_HOST: getEnvironmentVariable(
    'SERVICE_BUS_HOST',
    // Bare host: it's interpolated into an `sb://${host}/` AMQP endpoint, so a
    // scheme prefix here would produce a malformed `sb://https://...` URL.
    'service-bus.harmonicloud.com'
  ),
  DEV_INTEGRATION_URL: getEnvironmentVariable(
    'DEV_INTEGRATION_URL',
    'https://hooks.zapier.com/hooks/catch/9421537/oh28xxr/'
  ),
  API_SERVER:
    process.env.NODE_ENV === 'development'
      ? getEnvironmentVariable('API_SERVER', 'http://localhost:3002')
      : `https://api.${getEnvironmentVariable(
          'SERVER_NAME',
          window.location.hostname.replace('www.', '')
        )}`,
};

export default environment;

export const PERSIST_KEY = persistKeyForServerName(
  environment.SERVER_NAME,
  process.env.NODE_ENV === 'production'
);
